import { motion, useTransform } from "framer-motion";
import Tilt3D from "@/components/Tilt3D";
import FloatingChip from "@/components/kit/FloatingChip";
import { useMotionTier } from "@/lib/motionTier";
import { cn } from "@/lib/utils";

/**
 * PhoneStage — the hero object: a glass phone standing in perspective, its screen showing real product output (`children`),
 * with glass cards floating off it at different depths. CSS 3D only (no WebGL: that context belongs to the pose model).
 *
 *   tilt      { x, y } degrees it rests at; on load it rises from lying flat (700 ms, out-expo), then the chips pop out 80 ms apart
 *   chips     [{ icon, title, sub, z, float, position, showOn, mobile }] (FloatingChip props); `mobile` marks the ones kept on phones
 *   size      "md" 280 px, "lg" 380 px, "fluid" 30% of the window between 272 and 640 px (a bigger phone on a bigger screen, not more empty space),
 *             "tall" the same for a 9:16 screen, capped so the phone is never taller than the window minus the header; "story" the home scroll story's phone (78svh high at most on a phone)
 *   progress  optional MotionValue 0..1 (a scroll scene): the light sheen sweeps across the glass as it moves
 *   pulse     bump the number to flash the screen once (a choice was made)
 *   hue       a sport's hue (kit/sportTheme): tints the glow behind the phone instead of lime
 *
 * Motion follows the device (lib/motionTier): "full" tilts toward the pointer, "lite" (phones) keeps two chips and no tilt,
 * "static" is one still frame.
 */
const SIZE = { md: "w-[min(100%,280px)]", lg: "w-[min(100%,380px)]", fluid: "w-[clamp(272px,30vw,640px)] max-w-full",
  // a 9:16 screen: as wide as the window allows, but never taller than the window minus the header (so the whole phone fits on a laptop)
  tall: "w-[clamp(240px,min(30vw,calc((100svh-var(--nav-h,64px)-7.5rem)*0.5625)),640px)] max-w-full",
  // the home story: on a phone at most 78% of the screen's height (and 18 rem wide); from lg up it grows with the window but always fits under the header
  story: "w-[min(calc(100vw-3.5rem),calc(100svh*0.44),18rem)] lg:w-[clamp(260px,min(32vw,calc((100svh-var(--nav-h,64px)-5.5rem)*0.5625)),600px)] max-w-full" };
const SHEEN = "linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.32) 48%, transparent 66%)";

function Sheen({ progress }) {
  // function transforms: a plain range transform of opacity would be run by a native scroll timeline, which is not what we want here
  const opacity = useTransform(progress, (p) => 0.1 + 0.4 * p);
  const x = useTransform(progress, (p) => `${-35 + 70 * p}%`);
  return <motion.span aria-hidden="true" className="pointer-events-none absolute inset-0" style={{ opacity, x, background: SHEEN }} />;
}

export default function PhoneStage({ children, tilt = { x: 4, y: -10 }, chips = [], glow = true, size = "fluid", progress, pulse = 0, hue, allChips = false, className, ...rest }) {
  const tier = useMotionTier();
  const still = tier === "static";
  const shown = tier === "lite" && !allChips ? chips.filter((c) => c.mobile).slice(0, 2).map((c) => ({ ...c, showOn: "all" })) : chips;
  const Lean = tier === "full" ? Tilt3D : "div";
  const leanProps = tier === "full" ? { max: 6, glare: false, className: "relative" } : { className: "relative" };

  return (
    <div className={cn("relative mx-auto pb-7", SIZE[size] || SIZE.fluid, className)} style={{ perspective: 1100 }} data-phone-stage="true" {...rest}>
      {glow && <div aria-hidden="true" className={cn("pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[130%] aspect-square bg-[radial-gradient(closest-side,rgba(163,230,53,0.16),transparent)]", !still && "pf-breathe")} style={hue == null ? undefined : { background: `radial-gradient(closest-side, hsl(${hue} 85% 55% / 0.2), transparent)` }} />}
      <Lean {...leanProps}>
        <motion.div
          className="relative"
          style={{ transformStyle: "preserve-3d" }}
          initial={still ? false : { rotateX: 58, rotateY: tilt.y, y: 36, opacity: 0 }}
          animate={{ rotateX: tilt.x, rotateY: tilt.y, y: 0, opacity: 1 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          {/* the side of the phone: one copy of the body, pushed back */}
          <div aria-hidden="true" className="absolute inset-0 rounded-stage bg-zinc-700" style={{ transform: "translateZ(-14px)" }} />
          <div className="relative rounded-stage border border-white/15 bg-gradient-to-b from-zinc-800 to-zinc-900 p-2.5 shadow-float">
            <div className="relative rounded-[28px] overflow-hidden bg-zinc-950 ring-1 ring-black">
              {children}
              {progress ? <Sheen progress={progress} /> : <span aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.12]" style={{ background: SHEEN }} />}
              {pulse > 0 && <span key={pulse} aria-hidden="true" className="kit-pulse pointer-events-none absolute inset-0 bg-lime-400/25" />}
            </div>
          </div>
          {shown.map((c, i) => (
            <FloatingChip key={c.title} {...c} float={still ? null : c.float === undefined ? "a" : c.float} delay={still ? undefined : 600 + i * 80} />
          ))}
        </motion.div>
      </Lean>
      {/* the floor shadow */}
      <div aria-hidden="true" className="pointer-events-none absolute left-[10%] right-[10%] bottom-0 h-7 bg-[radial-gradient(closest-side,rgba(0,0,0,0.6),transparent)]" />
    </div>
  );
}
